import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FloatButton from "../components/ui/FloatButton";
import FloatCard from "../components/ui/FloatCard";
import { FaRegSquare, FaRegCheckSquare } from "react-icons/fa";
import { theme } from "../theme";
import styled from "styled-components";
import RenderIf from "../components/RenderIf";
import {WatchContext} from '../WatchContext'
import { MdClose } from "react-icons/md";
import BackToDash from "../components/BackToDash";

const Styles = {
  Button: styled(FloatButton)`
  font-weight: bold;
  `,
  TextBG: styled.span`
    background-color: ${({ color }) => color};
    padding: 0.5rem;
    width: 100%;

    text-align: center;
    overflow-wrap: anywhere;
    a {
      text-decoration: none;
      color: inherit;
      cursor: pointer;
    }
  `,
  Permit: styled.div`
    line-height: 2.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  CardCont: styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  `,
  ReqCard: styled.div`
    width: 100%;
    line-height: 2.5rem;
    display: flex;
    flex-direction: column;
    height: 100%;
    align-items: center;
    justify-items: flex-start;
  `,
  ButtonRow: styled.div`
    display: flex;
    justify-content: center;
  `,
  ModalWrapper: styled.div`
    width: 80rem;
    box-shadow: 0 5px 16px rgba(0, 0, 0, 0.2);
    background: #fff;
    color: #000;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    animation: 1s alternate fadein;
    @keyframes fadein {
      from {
        opacity: 0;
      }

      to {
        opacity: 100;
      }
    } ;
  `,
  ModalContent: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: left;
    margin: 2rem;
    line-height: 1.8;
    color: ${({ theme }) => theme.RUTeal};
    background: #f1f1f1;
    overflowy: scroll;
    h1 {
      font-size: 4rem;
      font-weight: 300;
    }

    p {
      margin-bottom: 1rem;
      font-size: 1.6rem;
      font-weight: 500;
    }
  `,
  Background: styled.div`
    width: 100vw;
    height: 100vh;
    background: #00000080;
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
  `,
  CloseModalButton: styled(MdClose)`
  cursor: pointer;
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  padding 0;
  z-index: 10;
  `,
  Table: styled.table`
    margin: 1rem;
    border: 1px solid black;
    border-collapse: collapse;

    thead th:nth-child(2),
    th:nth-child(3),
    th:nth-child(4),
    th:nth-child(5),
    th:nth-child(5) {
      width: 20%;
    }

    th,
    td {
      padding: 1.5rem;
      border: 1px solid black;
    }
    td a {
      line-break: anywhere;
    }
  `,
};

const Regulatory = () => {
  const navigate = useNavigate();
  
  const modalRef = useRef();

  const { permit, setPermit } = useContext(WatchContext);
  const [showModal, setShowModal] = useState(false);
  const [showAllPermits, setShowAllPermits] = useState(true)

  let selected = permit.permits.filter((permit) => permit.selected);
  let displayedPermits = showAllPermits ? permit.permits : selected
  
  const handleShowPermits = (value) => {
    if (value){
      setShowAllPermits(true)
    } else {
      setShowAllPermits(false)
    }
  } 

  const handleSelectPermit = (id) => {
      let updatedPermits = permit.permits.map((permit) => {
        if (permit.id === id) {
          permit.selected = true;
          // console.log(permit)
          return permit;
        } else {
          return permit;
        }
      });
      setPermit({ ...permit, permits: updatedPermits })
  };

  const handleRemovePermit = (id) => {
      let updatedPermits = permit.permits.map((permit) => {
        if (permit.id === id) {
          permit.selected = false;
          let reqs = permit.requirements.map((req) => {
            if (req.evaluated) {
              req.evaluated = false;
              return req;
            } else {
              return req;
            }
          });
          return { ...permit, requirements: reqs };
        } else {
          return permit;
        }
      });
      setPermit({ ...permit, permits: updatedPermits });
  };

  const handleUpdateTask = (permitId, taskId) => {
      let updatedPermits = permit.permits.map((permit) => {
        if (permit.id === permitId) {
          let reqs = permit.requirements.map((req) => {
            if (req.id === taskId) {
              if (req.evaluated) {
                req.evaluated = false;
              } else {
                req.evaluated = true;
              }
              return req;
            } else {
              return req;
            }
          });

          return { ...permit, requirements: reqs };
        } else {
          return permit;
        }
      });
      setPermit({ ...permit, permits: updatedPermits });
  };
  const openModal = () => {
    setShowModal((prev) => !prev);
  };

  // if you press escape key closes modal
  const keyPress = useCallback(
    (evt) => {
      if (evt.key === "Escape" && showModal) {
        setShowModal(false);
      }
    },
    [setShowModal, showModal]
  );

  useEffect(() => {
    document.addEventListener("keydown", keyPress);
    return () => document.removeEventListener("keydown", keyPress);
  }, [keyPress]);

  // click in background to close modal
  const closeModal = (evt) => {
    modalRef.current === evt.target && setShowModal(false);
  };

  useEffect(() => {
    const checkComplete = () => {
      // remove deselect from the array

      if (selected.length > 0) {
        setPermit({ ...permit, complete: true });
      }
    };
    checkComplete();
  }, []);

  let permitElems = displayedPermits.map((item) => (
    <FloatCard key={item.id} margin="1rem">
      <Styles.Permit>
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h2>
              Permit: <a href={item.permitLink}>{item.permit}</a>
            </h2>
            <h3>Type: {item.type}</h3>
            <RenderIf isTrue={item.selected}>
              <h3>Selected</h3>
            </RenderIf>
          </div>
          <RenderIf isTrue={!item.selected}>
             <FloatButton
             textColor='white'
             bgColor={theme.colors.pdeBlue}
            handleClick={() => handleSelectPermit(item.id)}
            padding="1rem 2rem"
            width="25rem"
            height="20rem"
            shadowColor={theme.colors.pdeBlue}
          >
            Select This Permit
          </FloatButton>
            
          </RenderIf>
          <RenderIf isTrue={item.selected}>
             <FloatButton
            shadowColor={theme.colors.ruRed}
            handleClick={() => handleRemovePermit(item.id)}
            width="200px"
          >
            De-select Permit
          </FloatButton>
            
          </RenderIf>
        </div>
        <Styles.CardCont>
          <Styles.Table>
            <thead>
              <tr>
                <th>Evaluated</th>
                <th>Requirement</th>
                <th>Attribute Nexus</th>
                <th>Metric Alignment</th>
                <th>Question or Need</th>
                <th>Information Resource</th>
              </tr>
            </thead>
            <tbody>
              {item.requirements.map((req) => (
                <tr key={req.id}>
                  {item.selected 
                  ? (<td onClick={() => handleUpdateTask(item.id, req.id)}>
                    { req.evaluated ? (
                      <FaRegCheckSquare
                        size={"3rem"}
                        color={theme.colors.greenGlow}
                      />
                    ) : (
                      <FaRegSquare size={"3rem"} color={theme.colors.grey4} />
                    )}
                  </td>) 
                  :
                  (<td></td>)
                  } 
                  <td>{req.requirement}</td>
                  <td>{req.attributeNexus}</td>
                  <td>{req.metricAlignment}</td>
                  <td>{req.questionNeed}</td>
                  <td>
                    <RenderIf isTrue={req.info?.length > 0}>
                      {req.info.includes("http") ? (
                        <a target="_blank" rel="noreferrer" href={req.info}>
                          {req.info}
                        </a>
                      ) : (
                        req.info
                      )}
                    </RenderIf>
                    <RenderIf isTrue={req.info2?.length > 0}>
                      <a target="_blank" rel="noreferrer" href={req.info2}>
                        {req.info2}
                      </a>
                    </RenderIf>
                  </td>
                </tr>
              ))}
            </tbody>
          </Styles.Table>
        </Styles.CardCont>
      </Styles.Permit>
    </FloatCard>
  ));

  return (
    <div>
      <Styles.ButtonRow>
      <BackToDash/>

        <FloatButton handleClick={openModal} width="200px">
          Permit Instructions
        </FloatButton>
        <RenderIf isTrue={displayedPermits.length === permit.permits.length}> 
        <FloatButton handleClick={() => handleShowPermits(false)} width="200px">
          <span style={{textAlign: 'center'}}>Show only selected permits</span>
        </FloatButton>
        </RenderIf>
        <RenderIf isTrue={displayedPermits.length < permit.permits.length}> 
        <FloatButton handleClick={() => handleShowPermits(true)} width="200px">
          <span style={{textAlign: 'center'}}>Show all permits</span>
        </FloatButton>
        </RenderIf>
        <RenderIf isTrue={showModal === true}>
          <Styles.Background ref={modalRef} onClick={closeModal}>
            <Styles.ModalWrapper showModal={showModal}>
              <Styles.ModalContent>
                <p>
                  The purpose of the regulatory checklist is to align the
                  information required by some permits with attributes and
                  metrics of WATCH. The goal of this checklist is to streamline
                  the data collecting process by identifying attributes that
                  meet assessment criteria as well regulatory inquiries. For
                  example, a WATCH user may analyze historical imagery to
                  quantify change for the horizontal position attribute, which,
                  if desired, is also required to obtain an Army Corps of
                  Engineers NWP27 permit for Bank Stabilization. By selecting a
                  permit on the Regulatory Checklist tab that the user thinks
                  may be appropriate for a future intervention, WATCH will track
                  the user’s progress in collecting information for those listed
                  permit requirements on the Output Summary Tab. Please note
                  that the regulatory checklist for each included permit, is NOT
                  a comprehensive list of permit requirements, but a truncated
                  list that identifies where specific items meet requirements
                  for a specific permit and an attribute-specific metric.
                </p>
                <div style={{ margin: "1rem" }}>
                  <h3>Steps:</h3>
                  <ol>
                    <li>
                      Select the appropriate permit using the button with the
                      words "Select This Permit".
                    </li>
                    <li>
                      Note all "Requirements" and their respective "Information
                      Resource" locations.
                    </li>
                    <li>
                      Note the "Attribute Nexus" and "Metric Alignment" for each
                      requirement.
                    </li>
                    <li>
                      As the user evaluates/compiles each item for a permit,
                      check the associated box in the "Evaluated".
                    </li>
                    <li>
                      When the "Evaluated" box for all permit items is checked,
                      the Permit section of the PDE WATCH dashboard will show
                      the tasks completed.
                    </li>
                  </ol>
                </div>
              </Styles.ModalContent>
              <Styles.CloseModalButton
                aria-label="close modal"
                onClick={() => setShowModal(false)}
              />
            </Styles.ModalWrapper>
          </Styles.Background>
        </RenderIf>
      </Styles.ButtonRow>
      <div>{permitElems}</div>
    </div>
  );
};

export default Regulatory;
